import moment from 'moment';
import React from 'react';
import { ELeadResponseType } from 'types/lead-response';

import BathtubOutlinedIcon from '@material-ui/icons/BathtubOutlined';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import HotelOutlinedIcon from '@material-ui/icons/HotelOutlined';
import HouseOutlinedIcon from '@material-ui/icons/HouseOutlined';
import LandscapeOutlinedIcon from '@material-ui/icons/LandscapeOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';

import AgentLandingLeadForm, { Eyebrow, normalizeAgents, valuationReportRequestOptions } from 'components/AgentLanding/AgentLandingPrimitives';
import { DigitalAdHeadlineMetrics, DigitalAdMarketCards } from 'components/AgentLanding/DigitalAd/DigitalAdZipCodeDataDisplay';

import { getAgentLandingMarketStats } from 'utils/api';
import { numberWithCommas, stringifyAddressObject } from 'utils/lib';

import HomeEstimationMap from './HomeEstimationMap';

// Download-report dependencies retained for future use:
// import html2canvas from 'html2canvas';
// import { downloadReportAgentLanding } from 'utils/api';
// import { openFileInNewWindow, showAPIErrorAlert } from 'utils/lib';

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const wholeCompactMoney = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const twoDecimalCompactMoney = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const oneDecimalCompactMoney = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const formatCompactEstimate = (value) => {
  if (value < 1000000) return `${wholeCompactMoney.format(value / 1000)}K`;
  if (value >= 10000000) return `${oneDecimalCompactMoney.format(value / 1000000)}M`;
  return `${twoDecimalCompactMoney.format(value / 1000000)}M`;
};
const roundEstimate = (value) => Math.round(value / 1000) * 1000;

const EstimateSlider = ({
  id, label, labels, value, onChange,
}) => (
  <div className="al-estimate-slider">
    <div className="al-estimate-slider-heading">
      <label htmlFor={id}>{label}</label>
      <span>{labels[value]}</span>
    </div>
    <input
      id={id}
      type="range"
      min="0"
      max="2"
      step="1"
      value={value}
      aria-valuetext={labels[value]}
      style={{ '--al-slider-progress': `${value * 50}%` }}
      onChange={(event) => onChange(Number(event.target.value))}
    />
    <div className="al-estimate-slider-labels" aria-hidden="true">
      {labels.map((option) => <span key={option}>{option}</span>)}
    </div>
  </div>
);

const splitComparableAddress = (propertyAddress = '') => {
  const [street, ...locality] = propertyAddress.split(',').map((part) => part.trim()).filter(Boolean);
  return { street: street || propertyAddress, locality: locality.join(', ') };
};

const HomeEstimation = ({
  agentLandingPageToken, agentData, propertyData, similarHomes,
}) => {
  const contactFormRef = React.useRef(null);
  // const mapRef = React.useRef(null);
  // const tableRef = React.useRef(null);
  // Props needed when restoring downloads: setIsDownloadingReport, eventId.
  const [marketStats, setMarketStats] = React.useState(null);
  const [marketStatsError, setMarketStatsError] = React.useState(false);
  const [propertyConditionSliderValue, setPropertyConditionSliderValue] = React.useState(1);
  const [hotMarketSliderValue, setHotMarketSliderValue] = React.useState(1);
  const agents = normalizeAgents(agentData);
  const isTeam = agents.length > 1;
  const displayedHomes = React.useMemo(() => [...(similarHomes || [])]
    .sort((left, right) => {
      if (!left.soldDate && !right.soldDate) return 0;
      if (!left.soldDate) return 1;
      if (!right.soldDate) return -1;
      return moment(right.soldDate).valueOf() - moment(left.soldDate).valueOf();
    })
    .slice(0, 4), [similarHomes]);

  React.useEffect(() => {
    const zipCode = propertyData?.address?.zipcode;
    if (!zipCode) return undefined;
    let active = true;
    setMarketStats(null);
    setMarketStatsError(false);
    getAgentLandingMarketStats(agentLandingPageToken, zipCode)
      .then((data) => {
        if (active) setMarketStats(data);
      })
      .catch(() => {
        if (active) setMarketStatsError(true);
      });
    return () => {
      active = false;
    };
  }, [agentLandingPageToken, propertyData?.address?.zipcode]);

  const valuation = React.useMemo(() => {
    if (!propertyData) return null;
    let midpoint;
    let pricePerSqft;
    let source = 'comparables';

    if (displayedHomes.length) {
      const validSqftComps = displayedHomes.filter((home) => home.squareFootage > 0 && home.price > 0);
      const averagePrice = displayedHomes.reduce((sum, home) => sum + home.price, 0) / displayedHomes.length;
      pricePerSqft = validSqftComps.length
        ? validSqftComps.reduce((sum, home) => sum + home.price, 0) / validSqftComps.reduce((sum, home) => sum + home.squareFootage, 0)
        : undefined;
      midpoint = propertyData.squareFootage && pricePerSqft ? propertyData.squareFootage * pricePerSqft : averagePrice;
    } else {
      pricePerSqft = Number(marketStats?.metrics?.medianPricePerSquareFoot);
      if (!(propertyData.squareFootage > 0) || !(pricePerSqft > 0)) return null;
      midpoint = propertyData.squareFootage * pricePerSqft;
      source = 'zip-median-price-per-square-foot';
    }

    return {
      low: midpoint * 0.9, high: midpoint * 1.1, midpoint, pricePerSqft, source,
    };
  }, [propertyData, displayedHomes, marketStats?.metrics?.medianPricePerSquareFoot]);

  const scrollToContactForm = () => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    contactFormRef.current?.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  /* Download-report implementation retained for future use.
  const onDownloadReport = async () => {
    if (!mapRef.current || !tableRef.current) return;
    setIsDownloadingReport(true);
    try {
      const [homeEstimateMapImage, similarHomesTableImage] = await Promise.all([
        html2canvas(mapRef.current, { useCORS: true, scale: 1.5 }).then((canvas) => canvas.toDataURL('image/png')),
        html2canvas(tableRef.current, { useCORS: true, scale: 1.5 }).then((canvas) => canvas.toDataURL('image/png')),
      ]);
      const { pdf } = await downloadReportAgentLanding(
        agentLandingPageToken,
        homeEstimateMapImage,
        similarHomesTableImage,
        eventId,
      );
      openFileInNewWindow(pdf);
    } catch (error) {
      showAPIErrorAlert(error);
    } finally {
      setIsDownloadingReport(false);
    }
  };
  */

  if (!propertyData) return <div className="al-empty-panel">Property details are not available. Please request a manual property review.</div>;

  const address = stringifyAddressObject(propertyData.address);
  // Shift the estimate by up to five percent in either direction while keeping
  // the market price-per-square-foot reference unchanged.
  const rangeAdjustment = 0.025 * (propertyConditionSliderValue - 1)
    + 0.025 * (hotMarketSliderValue - 1);
  const adjustedMidpoint = valuation?.midpoint * (1 + rangeAdjustment);
  const displayedValuation = valuation ? {
    low: roundEstimate(adjustedMidpoint * 0.9),
    midpoint: roundEstimate(adjustedMidpoint),
    high: roundEstimate(adjustedMidpoint * 1.1),
  } : null;
  const reportContext = displayedValuation
    ? `${valuation.source === 'comparables' ? 'Comparable property' : 'ZIP median price-per-square-foot'} report for ${address}; estimated range ${money.format(displayedValuation.low)}–${money.format(displayedValuation.high)}`
    : `Local market report for ${address}; valuation pricing was not available`;
  return (
    <main className="al-report-page">
      <section className="al-report-intro">
        <div className="al-shell al-report-hero">
          <div className="al-report-hero-copy">
            <div className="al-report-title">
              <div>
                <Eyebrow>Your local property snapshot</Eyebrow>
                <h1>
                  Your home value
                  <br />
                  has changed.
                </h1>
                <p>Personalized insights for your property and what is happening in the local market.</p>
              </div>
              <aside>
                <LockOutlinedIcon />
                <span>
                  <strong>Local data. Real insights.</strong>
                  <small>No obligation.</small>
                </span>
              </aside>
            </div>
            <article className="al-subject-summary">
              <div className="al-your-home" aria-label="Your home">
                <HomeOutlinedIcon />
                <span>YOUR HOME</span>
              </div>
              <div className="al-subject-address">
                <span>Your property</span>
                <h2>{propertyData.address.address1 || 'Your Home'}</h2>
                <p>
                  {propertyData.address.city}
                  ,
                  {' '}
                  {' '}
                  {propertyData.address.state}
                  {' '}
                  {' '}
                  {propertyData.address.zipcode}
                </p>
              </div>
              <div className="al-property-facts">
                {propertyData.numBeds > 0 && (
                  <div>
                    <HotelOutlinedIcon />
                    <strong>{propertyData.numBeds}</strong>
                    <span>Beds</span>
                  </div>
                )}
                {propertyData.numBaths > 0 && (
                  <div>
                    <BathtubOutlinedIcon />
                    <strong>{propertyData.numBaths}</strong>
                    <span>Baths</span>
                  </div>
                )}
                {propertyData.squareFootage > 0 && (
                  <div>
                    <HouseOutlinedIcon />
                    <strong>{numberWithCommas(propertyData.squareFootage)}</strong>
                    <span>Sq Ft</span>
                  </div>
                )}
                {propertyData.lotSquareFootage > 0 && (
                  <div>
                    <LandscapeOutlinedIcon />
                    <strong>{numberWithCommas(propertyData.lotSquareFootage)}</strong>
                    <span>Lot Sq Ft</span>
                  </div>
                )}
              </div>
            </article>
          </div>
          <div className="al-hero-map-wrap"><HomeEstimationMap className="al-subject-map" center={propertyData.address.coordinates} mode="subject" /></div>
        </div>
      </section>
      <div className="al-shell al-report-stack">
        {valuation && displayedValuation && (
          <section className="al-report-panel al-value-panel">
            <div className="al-value-copy">
              <span className="al-section-kicker al-value-kicker">Estimated property value</span>
              <h2>
                {money.format(displayedValuation.low)}
                {' '}
                –
                {' '}
                {money.format(displayedValuation.high)}
              </h2>
              {valuation.pricePerSqft && (
                <p>
                  <span style={{ whiteSpace: 'pre' }}>{'Estimated range · '}</span>
                  <b>
                    {`${money.format(displayedValuation.low / propertyData.squareFootage)} – ${money.format(displayedValuation.high / propertyData.squareFootage)} / sq ft`}
                  </b>
                </p>
              )}
              <button className="al-primary-button" type="button" aria-controls="full-valuation-report-form" onClick={scrollToContactForm}>Get my full report →</button>
            </div>
            <div className="al-range">
              <span className="al-midpoint">
                <strong>{formatCompactEstimate(displayedValuation.midpoint)}</strong>
                <span>Midpoint estimate</span>
              </span>
              <div className="al-range-line"><i /></div>
              <div className="al-range-labels">
                <span>
                  <b>{formatCompactEstimate(displayedValuation.low)}</b>
                  <br />
                  Low estimate
                </span>
                <span>
                  <b>{formatCompactEstimate(displayedValuation.high)}</b>
                  <br />
                  High estimate
                </span>
              </div>
              <p>
                {valuation.source === 'comparables'
                  ? 'Estimate is based on recent comparable transfers and available property facts. It is not an appraisal.'
                  : `Estimate is based on the median asking price per square foot for ZIP code ${propertyData.address.zipcode} and available property facts. It is not an appraisal.`}
              </p>
            </div>
            <div className="al-estimate-controls">
              <div className="al-estimate-controls-intro">
                <span className="al-section-kicker">Fine-tune the range</span>
                <p>Adjust for details that market data cannot fully capture.</p>
              </div>
              <EstimateSlider
                id="property-condition-estimate"
                label="How’s your property condition?"
                labels={['Poor', 'Average', 'Good']}
                value={propertyConditionSliderValue}
                onChange={setPropertyConditionSliderValue}
              />
              <EstimateSlider
                id="market-temperature-estimate"
                label="Are you in a hot market?"
                labels={['No', 'Kind of', 'Yes']}
                value={hotMarketSliderValue}
                onChange={setHotMarketSliderValue}
              />
            </div>
          </section>
        )}

        <section className="al-report-panel al-market-panel">
          <header className="al-section-heading">
            <div>
              <p className="al-eyebrow">
                What’s happening in
                {' '}
                {propertyData.address.zipcode}
              </p>
              <h2>Your neighborhood is moving.</h2>
            </div>
            {displayedHomes.length > 0 && (
              <span>
                {displayedHomes.length}
                {' '}
                {displayedHomes.length === 1 ? 'recent comparable property' : 'recent comparable properties'}
              </span>
            )}
          </header>
          {marketStats && <DigitalAdHeadlineMetrics zipCodeData={marketStats} />}
          {displayedHomes.length > 0 && (
            <>
              <h3 className="al-section-kicker">Recent activity in your neighborhood</h3>
              <HomeEstimationMap className="al-comps-map" center={propertyData.address.coordinates} similarHomes={displayedHomes} mode="comparables" />
              <div className="al-comp-grid">
                {displayedHomes.map((home, index) => {
                  const comparableAddress = splitComparableAddress(home.propertyAddress);
                  return (
                    <article className="al-comp-card" key={`${home.propertyAddress}-${index}`}>
                      <div className="al-comp-top">
                        <span className="al-comp-number">{index + 1}</span>
                        {home.soldDate && <small>{`Sold · ${moment(home.soldDate).format('MMM D, YYYY')}`}</small>}
                      </div>
                      <h4>{money.format(home.price)}</h4>
                      <p>
                        {comparableAddress.street}
                        {comparableAddress.locality && (
                          <>
                            <br />
                            {comparableAddress.locality}
                          </>
                        )}
                      </p>
                      <div className="al-comp-facts">
                        {home.numBeds > 0 && (
                          <span>
                            <HotelOutlinedIcon />
                            {`${home.numBeds} beds`}
                          </span>
                        )}
                        {home.numBaths > 0 && (
                          <span>
                            <BathtubOutlinedIcon />
                            {`${home.numBaths} baths`}
                          </span>
                        )}
                      </div>
                      <footer>
                        <span>{home.squareFootage > 0 ? `${numberWithCommas(home.squareFootage)} sq ft` : ''}</span>
                        {home.distanceMiles !== undefined && <strong>{`${home.distanceMiles.toFixed(1)} mi away`}</strong>}
                      </footer>
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </section>

        {(marketStats || marketStatsError) && (
          marketStats ? (
            <div className="al-results-grid">
              <DigitalAdMarketCards zipCodeData={marketStats} />
            </div>
          ) : (
            <p className="al-comparable-market-unavailable">Market trend data is not available for this ZIP code yet.</p>
          )
        )}

        <section id="full-valuation-report-form" className="al-report-panel al-report-form-wrap" ref={contactFormRef}>
          <AgentLandingLeadForm agentLandingPageToken={agentLandingPageToken} agentData={agentData} leadResponseType={ELeadResponseType.AgentLandingPropertyReviewRequest} title="Get the full valuation report." subtitle={`${isTeam ? 'We' : 'I'} can add property condition, improvements, and current buyer feedback to this data-led snapshot.`} submitLabel="Send my report" stepLabel="Your next step" privacyText="Your information is secure and will never be shared or sold." propertyAddress={address} reportContext={reportContext} showPropertyAddress={false} showSellingTimeline={false} showMessage={false} formVariant="report" requestOptions={valuationReportRequestOptions} />
          <aside className="al-report-agent">
            <p className="al-eyebrow">Questions about your value?</p>
            <h3>
              Talk with
              {' '}
              {isTeam ? 'us' : 'me'}
              .
            </h3>
            <p>Get a human perspective on the numbers—without the sales pressure.</p>
            <div className="al-mini-agent-list">
              {agents.map((agent) => (
                <div className="al-report-agent-identity" key={agent.firstName}>
                  {agent.pictureUrl ? (
                    <img src={agent.pictureUrl} alt={`Portrait of ${agent.firstName} ${agent.lastName || ''}`.trim()} />
                  ) : (
                    <span className="al-agent-fallback" aria-hidden="true">
                      {agent.firstName.charAt(0)}
                      {agent.lastName?.charAt(0)}
                    </span>
                  )}
                  <span className="al-report-agent-details">
                    <strong>
                      {agent.firstName}
                      {' '}
                      {agent.lastName}
                    </strong>
                    {agent.phoneNumber && (
                      <a href={`tel:${agent.phoneNumber}`}>
                        <PhoneOutlinedIcon aria-hidden="true" />
                        <span>{agent.phoneNumber}</span>
                      </a>
                    )}
                    {agent.email && (
                      <a href={`mailto:${agent.email}`}>
                        <EmailOutlinedIcon aria-hidden="true" />
                        <span>{agent.email}</span>
                      </a>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </aside>
        </section>
      </div>
    </main>
  );
};

export default HomeEstimation;
